import { useState, useEffect, useCallback } from 'react';
import FeatherIcon from 'feather-icons-react';

const TABS = [
  { id: 'basics', label: 'Basics', icon: '🏢' },
  { id: 'floor_plans', label: 'Floor Plans', icon: '📐' },
  { id: 'amenities', label: 'Amenities', icon: '✨' },
  { id: 'policies', label: 'Policies', icon: '📋' },
  { id: 'location', label: 'Location', icon: '📍' },
  { id: 'community', label: 'Community', icon: '👥' },
  { id: 'action_links', label: 'Links', icon: '🔗' },
  { id: 'ai_content', label: 'AI Content', icon: '🤖' },
];

const AMENITY_CATEGORIES = [
  { value: 'building', label: 'Building', icon: '🏢' },
  { value: 'suite', label: 'Suite', icon: '🛋️' },
  { value: 'outdoor', label: 'Outdoor', icon: '🌿' },
  { value: 'community', label: 'Community', icon: '🤝' },
  { value: 'pet', label: 'Pet', icon: '🐾' },
  { value: 'parking', label: 'Parking', icon: '🚗' },
  { value: 'other', label: 'Other', icon: '⚙️' },
];

const NEARBY_CATEGORIES = [
  { value: 'school', icon: '🎓' },
  { value: 'grocery', icon: '🛒' },
  { value: 'bank', icon: '🏦' },
  { value: 'pharmacy', icon: '💊' },
  { value: 'gas_station', icon: '⛽' },
  { value: 'park', icon: '🌳' },
  { value: 'restaurant', icon: '🍽️' },
  { value: 'transit', icon: '🚌' },
  { value: 'gym', icon: '🏋️' },
  { value: 'other', icon: '📍' },
];

const nearbyIcon = (cat) => NEARBY_CATEGORIES.find(c => c.value === cat)?.icon || '📍';

const emptyEntry = {
  property_name: '',
  slug: '',
  property_type: 'apartment',
  city: '',
  province: 'AB',
  address: '',
  postal_code: '',
  geo: { lat: '', lng: '' },
  neighbourhood: '',
  area: '',
  is_active: true,
  total_units: '',
  available_units_count: '',
  available_from: '',
  floor_plans: [],
  amenities: [],
  nearby_places: [],
  transit_info: '',
  walk_score: '',
  pet_policy: {
    pets_allowed: false,
    allowed_types: [],
    max_weight_kg: '',
    pet_deposit: '',
    monthly_pet_fee: '',
    restrictions: '',
  },
  resident_parking: {
    available: false,
    type: 'none',
    monthly_cost: '',
    stalls_available: '',
    ev_charging: false,
  },
  visitor_parking: {
    available: false,
    details: '',
    max_hours: '',
    cost_per_hour: '',
    free: false,
  },
  utilities_included: {
    heat: false,
    water: false,
    electricity: false,
    internet: false,
    trash: true,
    notes: '',
  },
  lease_terms: {
    min_lease_months: 12,
    max_lease_months: '',
    short_term_available: false,
    security_deposit: '',
    move_in_specials: '',
    application_fee: '',
  },
  smoking_policy: 'non_smoking',
  laundry: 'shared',
  action_links: {
    property_page_url: '',
    book_tour_url: '',
    apply_now_url: '',
    virtual_tour_url: '',
    request_maintenance_url: '',
    get_insurance_url: '',
    discount_cards_url: '',
  },
  community_leader: {
    name: '',
    bio: '',
    phone: '',
    email: '',
    office_hours: '',
  },
  events: [],
  why_choose_us: { headline: '', points: [] },
  property_features: [],
  ai_summary: '',
  ai_highlights: [],
  faqs: [],
};

const generateSlug = (name) =>
  name.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const Toggle = ({ checked, onChange, label }) => (
  <label className="kb-toggle">
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
    />
    <div
      className={`kb-toggle__track${checked ? ' kb-toggle--checked' : ''}`}
      style={checked ? { background: '#059669' } : {}}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3, width: 18, height: 18,
        borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        transition: 'left .2s'
      }} />
    </div>
    {label && <span className="kb-toggle__label">{label}</span>}
  </label>
);

const Field = ({ label, required, hint, error, children, span }) => (
  <div className="kb-field" style={span ? { gridColumn: `span ${span}` } : {}}>
    {label && (
      <label className="kb-field__label">
        {label}{required && <span>*</span>}
      </label>
    )}
    {children}
    {hint && <div className="kb-field__hint">{hint}</div>}
    {error && <div className="kb-field__error">⚠ {error}</div>}
  </div>
);

const ListEditor = ({ items, onChange, placeholder }) => {
  const add = () => onChange([...items, '']);
  const update = (i, v) => { const n = [...items]; n[i] = v; onChange(n); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="kb-list-editor">
      {items.map((item, i) => (
        <div key={i} className="kb-list-editor__item">
          <span className="kb-list-editor__drag">⠿</span>
          <input
            className="kb-list-editor__input"
            value={item}
            onChange={e => update(i, e.target.value)}
            placeholder={placeholder || 'Enter value…'}
          />
          <button className="kb-list-editor__remove" onClick={() => remove(i)}>×</button>
        </div>
      ))}
      <button className="kb-list-editor__add" onClick={add}>
        <FeatherIcon icon="plus" size={13} /> Add item
      </button>
    </div>
  );
};

const KnowledgeBaseFormModal = ({ open, entry, onClose, onSave, userRole }) => {
  const [activeTab, setActiveTab] = useState('basics');
  const [form, setForm] = useState(emptyEntry);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveTab('basics');
      setErrors({});
      if (entry) {
        const merged = { ...emptyEntry, ...entry };
        merged.geo = { lat: entry.geo?.lat || '', lng: entry.geo?.lng || '' };
        merged.area = entry.area || '';
        merged.pet_policy = { ...emptyEntry.pet_policy, ...(entry.pet_policy || {}) };
        merged.resident_parking = { ...emptyEntry.resident_parking, ...(entry.resident_parking || {}) };
        merged.visitor_parking = { ...emptyEntry.visitor_parking, ...(entry.visitor_parking || {}) };
        merged.utilities_included = { ...emptyEntry.utilities_included, ...(entry.utilities_included || {}) };
        merged.lease_terms = { ...emptyEntry.lease_terms, ...(entry.lease_terms || {}) };
        merged.action_links = { ...emptyEntry.action_links, ...(entry.action_links || {}) };
        merged.community_leader = { ...emptyEntry.community_leader, ...(entry.community_leader || {}) };
        merged.why_choose_us = { headline: entry.why_choose_us?.headline || '', points: entry.why_choose_us?.points || [] };
        merged.available_from = entry.available_from ? new Date(entry.available_from).toISOString().split('T')[0] : '';
        setForm(merged);
      } else {
        setForm(emptyEntry);
      }
    }
  }, [open, entry]);

  const set = useCallback((path, value) => {
    setForm(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
    if (errors[path]) setErrors(prev => { const n = { ...prev }; delete n[path]; return n; });
  }, [errors]);

  const handleNameChange = (v) => {
    set('property_name', v);
    if (!entry?._id) set('slug', generateSlug(v));
  };

  const validate = () => {
    const e = {};
    if (!form.property_name?.trim()) e.property_name = 'Property name is required';
    if (!form.city?.trim()) e.city = 'City is required';
    if (!form.address?.trim()) e.address = 'Address is required';
    if (!form.property_type) e.property_type = 'Property type is required';
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setActiveTab('basics');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.geo.lat) delete payload.geo;
      if (!payload.available_from) payload.available_from = null;
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="kb-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="kb-modal">
        <div className="kb-modal__header">
          <div className="kb-modal__header-left">
            <div className="kb-modal__header-icon">
              {entry?._id ? '✏️' : '🏛️'}
            </div>
            <div>
              <h2 className="kb-modal__title">
                {entry?._id ? 'Edit Property' : 'New Property'}
              </h2>
              <p className="kb-modal__subtitle">
                {entry?._id ? `Editing: ${entry.property_name}` : 'Configure your AI knowledge base entry'}
              </p>
            </div>
          </div>
          <button className="kb-modal__close" onClick={onClose}>
            <FeatherIcon icon="x" size={16} />
          </button>
        </div>

        <div className="kb-modal__tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`kb-modal__tab${activeTab === tab.id ? ' kb-modal__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="kb-modal__tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="kb-modal__body">
          {activeTab === 'basics' && <BasicsTab form={form} set={set} errors={errors} handleNameChange={handleNameChange} entry={entry} />}
          {activeTab === 'floor_plans' && <FloorPlansTab form={form} setForm={setForm} />}
          {activeTab === 'amenities' && <AmenitiesTab form={form} setForm={setForm} />}
          {activeTab === 'policies' && <PoliciesTab form={form} set={set} />}
          {activeTab === 'location' && <LocationTab form={form} set={set} setForm={setForm} />}
          {activeTab === 'community' && <CommunityTab form={form} set={set} setForm={setForm} />}
          {activeTab === 'ai_content' && <AIContentTab form={form} set={set} setForm={setForm} />}
          {activeTab === 'action_links' && <ActionLinksTab form={form} set={set} />}
        </div>

        <div className="kb-modal__footer">
          <button className="kb-btn kb-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="kb-btn kb-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
                Saving…
              </>
            ) : (
              <>
                <FeatherIcon icon="save" size={14} />
                {entry?._id ? 'Save Changes' : 'Create Property'}
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const BasicsTab = ({ form, set, errors, handleNameChange, entry }) => (
  <div>
    <div className="kb-section">
      <div className="kb-section__title">🏢 Property Identity</div>
      <div className="kb-form-grid kb-form-grid--2">
        <Field label="Property Name" required error={errors.property_name}>
          <input className={`kb-input${errors.property_name ? ' kb-input--error' : ''}`}
            value={form.property_name} onChange={e => handleNameChange(e.target.value)}
            placeholder="e.g. The Summit Residences" />
        </Field>
        <Field label="URL Slug" hint="Auto-generated from name. Must be unique." error={errors.slug}>
          <input className="kb-input" value={form.slug}
            onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="the-summit-residences" />
        </Field>
        <Field label="Property Type" required error={errors.property_type}>
          <select className="kb-select" value={form.property_type} onChange={e => set('property_type', e.target.value)}>
            <option value="apartment">🏢 Apartment</option>
            <option value="villa">🏡 Villa</option>
            <option value="townhouse">🏘️ Townhouse</option>
            <option value="condo">🏙️ Condo</option>
            <option value="mixed">🏗️ Mixed Use</option>
          </select>
        </Field>
        <Field label="Status">
          <div style={{ paddingTop: 8 }}>
            <Toggle checked={form.is_active} onChange={v => set('is_active', v)} label={form.is_active ? 'Active (visible to AI)' : 'Inactive (hidden)'} />
          </div>
        </Field>
      </div>
    </div>

    <div className="kb-section">
      <div className="kb-section__title">📍 Address</div>
      <div className="kb-form-grid kb-form-grid--2">
        <Field label="Street Address" required error={errors.address} span={2}>
          <input className={`kb-input${errors.address ? ' kb-input--error' : ''}`}
            value={form.address} onChange={e => set('address', e.target.value)}
            placeholder="123 Main Street NW" />
        </Field>
        <Field label="City" required error={errors.city}>
          <input className={`kb-input${errors.city ? ' kb-input--error' : ''}`}
            value={form.city} onChange={e => set('city', e.target.value)}
            placeholder="Edmonton" />
        </Field>
        <Field label="Province">
          <select className="kb-select" value={form.province} onChange={e => set('province', e.target.value)}>
            {['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'].map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>
        <Field label="Postal Code">
          <input className="kb-input" value={form.postal_code} onChange={e => set('postal_code', e.target.value)} placeholder="T2P 1J9" />
        </Field>
        <Field label="Neighbourhood" hint="Sub-neighbourhood name (optional, used as fallback area label).">
          <input className="kb-input" value={form.neighbourhood} onChange={e => set('neighbourhood', e.target.value)} placeholder="Beltline, Kensington…" />
        </Field>
        <Field label="Area" hint="Primary area label shown to the AI agent. Overrides neighbourhood. e.g. Downtown, Strathcona, Glastonbury.">
          <input className="kb-input" value={form.area} onChange={e => set('area', e.target.value)} placeholder="e.g. Downtown" />
        </Field>
        <Field label="Latitude">
          <input className="kb-input" type="number" step="any" value={form.geo?.lat || ''} onChange={e => set('geo.lat', e.target.value)} placeholder="51.0447" />
        </Field>
        <Field label="Longitude">
          <input className="kb-input" type="number" step="any" value={form.geo?.lng || ''} onChange={e => set('geo.lng', e.target.value)} placeholder="-114.0719" />
        </Field>
      </div>
    </div>

    <div className="kb-section">
      <div className="kb-section__title">📊 Inventory</div>
      <div className="kb-form-grid kb-form-grid--3">
        <Field label="Total Units">
          <input className="kb-input" type="number" min={0} value={form.total_units} onChange={e => set('total_units', e.target.value)} placeholder="48" />
        </Field>
        <Field label="Available Units">
          <input className="kb-input" type="number" min={0} value={form.available_units_count} onChange={e => set('available_units_count', e.target.value)} placeholder="12" />
        </Field>
        <Field label="Available From">
          <input className="kb-input" type="date" value={form.available_from} onChange={e => set('available_from', e.target.value)} />
        </Field>
      </div>
    </div>

    <div className="kb-section">
      <div className="kb-section__title">🏠 Building Features</div>
      <div className="kb-form-grid kb-form-grid--3">
        <Field label="Laundry">
          <select className="kb-select" value={form.laundry} onChange={e => set('laundry', e.target.value)}>
            <option value="in_suite">In-Suite</option>
            <option value="shared">Shared</option>
            <option value="none">None</option>
          </select>
        </Field>
        <Field label="Smoking Policy">
          <select className="kb-select" value={form.smoking_policy} onChange={e => set('smoking_policy', e.target.value)}>
            <option value="non_smoking">Non-Smoking</option>
            <option value="outdoor_only">Outdoor Only</option>
            <option value="allowed">Smoking Allowed</option>
          </select>
        </Field>
        <Field label="Walk Score (0-100)">
          <input className="kb-input" type="number" min={0} max={100} value={form.walk_score} onChange={e => set('walk_score', e.target.value)} placeholder="82" />
        </Field>
      </div>
    </div>
  </div>
);

const emptyFloorPlan = {
  label: '',
  bedrooms: 0,
  bathrooms: 1,
  sq_ft_min: '',
  sq_ft_max: '',
  rent_min: '',
  rent_max: '',
  availability_status: 'available',
  units_available: 0,
  units_total: 0,
  security_deposit: '',
  virtual_tour_url: '',
  features: [],
  incentives: [],
  current_offers: [],
};

const FloorPlansTab = ({ form, setForm }) => {
  const [expanded, setExpanded] = useState(null);

  const addPlan = () => {
    const plan = { ...emptyFloorPlan, _tempId: Date.now() };
    setForm(prev => ({ ...prev, floor_plans: [...prev.floor_plans, plan] }));
    setExpanded(form.floor_plans.length);
  };

  const updatePlan = (i, field, value) => {
    setForm(prev => {
      const plans = [...prev.floor_plans];
      plans[i] = { ...plans[i], [field]: value };
      return { ...prev, floor_plans: plans };
    });
  };

  const removePlan = (i) => {
    setForm(prev => ({ ...prev, floor_plans: prev.floor_plans.filter((_, idx) => idx !== i) }));
    setExpanded(null);
  };

  return (
    <div>
      <div className="kb-section">
        <div className="kb-section__title">📐 Floor Plans</div>
        <div className="kb-floor-plans">
          {form.floor_plans.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF', fontSize: 14 }}>
              No floor plans yet. Add one below.
            </div>
          )}
          {form.floor_plans.map((plan, i) => (
            <FloorPlanCard
              key={plan._id || plan._tempId || i}
              plan={plan}
              index={i}
              isExpanded={expanded === i}
              onToggle={() => setExpanded(expanded === i ? null : i)}
              onUpdate={(field, val) => updatePlan(i, field, val)}
              onRemove={() => removePlan(i)}
            />
          ))}
          <button className="kb-btn kb-btn--ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={addPlan}>
            <FeatherIcon icon="plus" size={14} /> Add Floor Plan
          </button>
        </div>
      </div>
    </div>
  );
};

const FloorPlanCard = ({ plan, index, isExpanded, onToggle, onUpdate, onRemove }) => (
  <div className="kb-floor-plan-card">
    <div className="kb-floor-plan-card__header" onClick={onToggle}>
      <div className="kb-floor-plan-card__label">
        {plan.label || `Floor Plan ${index + 1}`}
        <span style={{ fontWeight: 400, color: '#6B7280', marginLeft: 8, fontSize: 13 }}>
          {plan.bedrooms === 0 ? 'Studio' : `${plan.bedrooms}BR`} · {plan.bathrooms}BA
          {plan.sq_ft_min ? ` · ${plan.sq_ft_min}${plan.sq_ft_max ? `–${plan.sq_ft_max}` : ''} sqft` : ''}
        </span>
      </div>
      <div className="kb-floor-plan-card__meta">
        {plan.rent_min ? `$${Number(plan.rent_min).toLocaleString()}${plan.rent_max ? `–$${Number(plan.rent_max).toLocaleString()}` : ''}/mo` : 'No price'}
      </div>
      <span className={`kb-floor-plan-card__status kb-floor-plan-card__status--${plan.availability_status}`}>
        {plan.availability_status?.replace('_', ' ')}
      </span>
      <span className={`kb-floor-plan-card__chevron${isExpanded ? ' kb-floor-plan-card__chevron--open' : ''}`}>
        <FeatherIcon icon="chevron-down" size={16} />
      </span>
    </div>

    {isExpanded && (
      <div className="kb-floor-plan-card__body">
        <div className="kb-form-grid kb-form-grid--3">
          <Field label="Label">
            <input className="kb-input" value={plan.label} onChange={e => onUpdate('label', e.target.value)} placeholder="1 Bedroom A" />
          </Field>
          <Field label="Bedrooms">
            <input className="kb-input" type="number" min={0} value={plan.bedrooms} onChange={e => onUpdate('bedrooms', +e.target.value)} />
          </Field>
          <Field label="Bathrooms">
            <input className="kb-input" type="number" min={1} step={0.5} value={plan.bathrooms} onChange={e => onUpdate('bathrooms', +e.target.value)} />
          </Field>
          <Field label="Sq Ft Min">
            <input className="kb-input" type="number" value={plan.sq_ft_min} onChange={e => onUpdate('sq_ft_min', e.target.value)} placeholder="650" />
          </Field>
          <Field label="Sq Ft Max">
            <input className="kb-input" type="number" value={plan.sq_ft_max} onChange={e => onUpdate('sq_ft_max', e.target.value)} placeholder="750" />
          </Field>
          <Field label="Rent Min ($)">
            <input className="kb-input" type="number" value={plan.rent_min} onChange={e => onUpdate('rent_min', +e.target.value)} placeholder="1400" />
          </Field>
          <Field label="Rent Max ($)">
            <input className="kb-input" type="number" value={plan.rent_max} onChange={e => onUpdate('rent_max', +e.target.value)} placeholder="1600" />
          </Field>
          <Field label="Security Deposit ($)">
            <input className="kb-input" type="number" value={plan.security_deposit} onChange={e => onUpdate('security_deposit', e.target.value)} placeholder="1400" />
          </Field>
          <Field label="Availability">
            <select className="kb-select" value={plan.availability_status} onChange={e => onUpdate('availability_status', e.target.value)}>
              <option value="available">Available</option>
              <option value="waitlist">Waitlist</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </Field>
          <Field label="Units Available">
            <input className="kb-input" type="number" min={0} value={plan.units_available} onChange={e => onUpdate('units_available', +e.target.value)} />
          </Field>
          <Field label="Units Total">
            <input className="kb-input" type="number" min={0} value={plan.units_total} onChange={e => onUpdate('units_total', +e.target.value)} />
          </Field>
          <Field label="Virtual Tour URL" span={3}>
            <input className="kb-input" value={plan.virtual_tour_url} onChange={e => onUpdate('virtual_tour_url', e.target.value)} placeholder="https://…" />
          </Field>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="kb-form-grid kb-form-grid--3">
            <Field label="Features">
              <ListEditor items={plan.features} onChange={v => onUpdate('features', v)} placeholder="e.g. Private balcony" />
            </Field>
            <Field label="Incentives">
              <ListEditor items={plan.incentives} onChange={v => onUpdate('incentives', v)} placeholder="e.g. First month free" />
            </Field>
            <Field label="Current Offers">
              <ListEditor items={plan.current_offers} onChange={v => onUpdate('current_offers', v)} placeholder="e.g. $500 move-in bonus" />
            </Field>
          </div>
        </div>

        <div className="kb-floor-plan-card__actions">
          <button className="kb-btn kb-btn--danger kb-btn--sm" onClick={onRemove}>
            <FeatherIcon icon="trash-2" size={13} /> Remove Floor Plan
          </button>
        </div>
      </div>
    )}
  </div>
);

const AmenitiesTab = ({ form, setForm }) => {
  const [newCat, setNewCat] = useState('building');
  const [newName, setNewName] = useState('');

  const addAmenity = () => {
    if (!newName.trim()) return;
    const amenity = { category: newCat, name: newName.trim(), icon_key: '', is_highlighted: false, _tempId: Date.now() };
    setForm(prev => ({ ...prev, amenities: [...prev.amenities, amenity] }));
    setNewName('');
  };

  const toggleHighlight = (i) => {
    setForm(prev => {
      const a = [...prev.amenities];
      a[i] = { ...a[i], is_highlighted: !a[i].is_highlighted };
      return { ...prev, amenities: a };
    });
  };

  const removeAmenity = (i) => {
    setForm(prev => ({ ...prev, amenities: prev.amenities.filter((_, idx) => idx !== i) }));
  };

  const grouped = AMENITY_CATEGORIES.map(cat => ({
    ...cat,
    items: form.amenities.filter((a, i) => ({ ...a, _i: i })).map((a, i) => ({ ...a, _i: i })).filter(a => a.category === cat.value),
  })).filter(g => g.items.length > 0);

  return (
    <div>
      <div className="kb-section">
        <div className="kb-section__title">✨ Add Amenity</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Category" style={{ flex: '0 0 160px' }}>
            <select className="kb-select" value={newCat} onChange={e => setNewCat(e.target.value)} style={{ minWidth: 140 }}>
              {AMENITY_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
              ))}
            </select>
          </Field>
          <div className="kb-field" style={{ flex: 1, minWidth: 200 }}>
            <label className="kb-field__label">Amenity Name</label>
            <input className="kb-input" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Rooftop Terrace, Gym, Concierge…"
              onKeyDown={e => e.key === 'Enter' && addAmenity()} />
          </div>
          <button className="kb-btn kb-btn--primary" onClick={addAmenity} style={{ marginBottom: 0, height: 38 }}>
            <FeatherIcon icon="plus" size={14} /> Add
          </button>
        </div>
      </div>

      {grouped.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF', fontSize: 14 }}>
          No amenities yet. Add some above — they power your AI agent's responses.
        </div>
      )}

      {grouped.map(group => (
        <div key={group.value} className="kb-section">
          <div className="kb-section__title">{group.icon} {group.label}</div>
          <div className="kb-amenity-grid">
            {group.items.map((amenity) => {
              const idx = form.amenities.indexOf(amenity);
              return (
                <div key={amenity._id || amenity._tempId || amenity.name}
                  className={`kb-amenity-item${amenity.is_highlighted ? ' kb-amenity-item--highlighted' : ''}`}
                  onClick={() => toggleHighlight(idx)}
                >
                  <span className="kb-amenity-item__cat">{group.icon}</span>
                  <span className="kb-amenity-item__name">{amenity.name}</span>
                  {amenity.is_highlighted && <span className="kb-amenity-item__star">★</span>}
                  <button style={{ border: 'none', background: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1, marginLeft: 4 }}
                    onClick={e => { e.stopPropagation(); removeAmenity(idx); }}>×</button>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>Click an amenity to toggle "highlighted" (shown prominently to AI)</div>
        </div>
      ))}
    </div>
  );
};

const PoliciesTab = ({ form, set }) => (
  <div>
    <div className="kb-section">
      <div className="kb-section__title">🐾 Pet Policy</div>
      <div style={{ marginBottom: 14 }}>
        <Toggle checked={form.pet_policy?.pets_allowed} onChange={v => set('pet_policy.pets_allowed', v)} label="Pets Allowed" />
      </div>
      {form.pet_policy?.pets_allowed && (
        <div className="kb-form-grid kb-form-grid--3">
          <Field label="Max Weight (kg)">
            <input className="kb-input" type="number" value={form.pet_policy?.max_weight_kg || ''} onChange={e => set('pet_policy.max_weight_kg', e.target.value)} placeholder="25" />
          </Field>
          <Field label="Pet Deposit ($)">
            <input className="kb-input" type="number" value={form.pet_policy?.pet_deposit || ''} onChange={e => set('pet_policy.pet_deposit', e.target.value)} placeholder="300" />
          </Field>
          <Field label="Monthly Pet Fee ($)">
            <input className="kb-input" type="number" value={form.pet_policy?.monthly_pet_fee || ''} onChange={e => set('pet_policy.monthly_pet_fee', e.target.value)} placeholder="50" />
          </Field>
          <Field label="Allowed Types (comma separated)" span={2}>
            <input className="kb-input" value={(form.pet_policy?.allowed_types || []).join(', ')}
              onChange={e => set('pet_policy.allowed_types', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="Dogs, Cats, Small Animals" />
          </Field>
          <Field label="Restrictions">
            <input className="kb-input" value={form.pet_policy?.restrictions || ''} onChange={e => set('pet_policy.restrictions', e.target.value)} placeholder="No Pit Bulls" />
          </Field>
        </div>
      )}
    </div>

    <div className="kb-section">
      <div className="kb-section__title">🚗 Resident Parking</div>
      <div style={{ marginBottom: 14 }}>
        <Toggle checked={form.resident_parking?.available} onChange={v => set('resident_parking.available', v)} label="Parking Available" />
      </div>
      {form.resident_parking?.available && (
        <div className="kb-form-grid kb-form-grid--3">
          <Field label="Type">
            <select className="kb-select" value={form.resident_parking?.type} onChange={e => set('resident_parking.type', e.target.value)}>
              <option value="underground_heated">Underground Heated</option>
              <option value="underground">Underground</option>
              <option value="surface">Surface</option>
              <option value="covered">Covered</option>
              <option value="garage">Garage</option>
              <option value="none">None</option>
            </select>
          </Field>
          <Field label="Monthly Cost ($)">
            <input className="kb-input" type="number" value={form.resident_parking?.monthly_cost || ''} onChange={e => set('resident_parking.monthly_cost', e.target.value)} placeholder="125" />
          </Field>
          <Field label="Stalls Available">
            <input className="kb-input" type="number" value={form.resident_parking?.stalls_available || ''} onChange={e => set('resident_parking.stalls_available', e.target.value)} />
          </Field>
          <div style={{ paddingTop: 22 }}>
            <Toggle checked={form.resident_parking?.ev_charging} onChange={v => set('resident_parking.ev_charging', v)} label="EV Charging Available" />
          </div>
        </div>
      )}
    </div>

    <div className="kb-section">
      <div className="kb-section__title">🅿️ Visitor Parking</div>
      <div style={{ marginBottom: 14 }}>
        <Toggle checked={form.visitor_parking?.available} onChange={v => set('visitor_parking.available', v)} label="Visitor Parking Available" />
      </div>
      {form.visitor_parking?.available && (
        <div className="kb-form-grid kb-form-grid--3">
          <Field label="Details" span={2}>
            <input className="kb-input" value={form.visitor_parking?.details || ''} onChange={e => set('visitor_parking.details', e.target.value)} placeholder="First 2 hours free in underground lot" />
          </Field>
          <Field label="Max Hours">
            <input className="kb-input" type="number" value={form.visitor_parking?.max_hours || ''} onChange={e => set('visitor_parking.max_hours', e.target.value)} placeholder="4" />
          </Field>
          <Field label="Cost Per Hour ($)">
            <input className="kb-input" type="number" step="0.25" value={form.visitor_parking?.cost_per_hour || ''} onChange={e => set('visitor_parking.cost_per_hour', e.target.value)} placeholder="2.00" />
          </Field>
          <div style={{ paddingTop: 22 }}>
            <Toggle checked={form.visitor_parking?.free} onChange={v => set('visitor_parking.free', v)} label="Free Visitor Parking" />
          </div>
        </div>
      )}
    </div>

    <div className="kb-section">
      <div className="kb-section__title">⚡ Utilities Included</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 14 }}>
        {[['heat', '🔥 Heat'], ['water', '💧 Water'], ['electricity', '⚡ Electricity'], ['internet', '📶 Internet'], ['trash', '🗑️ Trash']].map(([key, lbl]) => (
          <Toggle key={key} checked={form.utilities_included?.[key]} onChange={v => set(`utilities_included.${key}`, v)} label={lbl} />
        ))}
      </div>
      <Field label="Utilities Notes">
        <input className="kb-input" value={form.utilities_included?.notes || ''} onChange={e => set('utilities_included.notes', e.target.value)} placeholder="e.g. Heat and water included, electricity separately metered" />
      </Field>
    </div>

    <div className="kb-section">
      <div className="kb-section__title">📄 Lease Terms</div>
      <div className="kb-form-grid kb-form-grid--3">
        <Field label="Min Lease (months)">
          <input className="kb-input" type="number" value={form.lease_terms?.min_lease_months || ''} onChange={e => set('lease_terms.min_lease_months', +e.target.value)} placeholder="12" />
        </Field>
        <Field label="Max Lease (months)">
          <input className="kb-input" type="number" value={form.lease_terms?.max_lease_months || ''} onChange={e => set('lease_terms.max_lease_months', e.target.value)} placeholder="24" />
        </Field>
        <Field label="Application Fee ($)">
          <input className="kb-input" type="number" value={form.lease_terms?.application_fee || ''} onChange={e => set('lease_terms.application_fee', +e.target.value)} placeholder="0" />
        </Field>
        <Field label="Security Deposit ($)">
          <input className="kb-input" type="number" value={form.lease_terms?.security_deposit || ''} onChange={e => set('lease_terms.security_deposit', e.target.value)} placeholder="1400" />
        </Field>
        <Field label="Move-In Specials" span={2}>
          <input className="kb-input" value={form.lease_terms?.move_in_specials || ''} onChange={e => set('lease_terms.move_in_specials', e.target.value)} placeholder="First month free on 12-month lease" />
        </Field>
        <div style={{ paddingTop: 22 }}>
          <Toggle checked={form.lease_terms?.short_term_available} onChange={v => set('lease_terms.short_term_available', v)} label="Short-Term Available" />
        </div>
      </div>
    </div>
  </div>
);

const NearbyPlaceRow = ({ place, onRemove }) => (
  <div className="kb-nearby-item">
    <span className="kb-nearby-item__emoji">{nearbyIcon(place.category)}</span>
    <div className="kb-nearby-item__info">
      <div className="kb-nearby-item__name">{place.name}</div>
      <div className="kb-nearby-item__dist">{place.distance_label || place.category}</div>
    </div>
    <button className="kb-nearby-item__remove" onClick={onRemove}>×</button>
  </div>
);

const LocationTab = ({ form, set, setForm }) => {
  const [newPlace, setNewPlace] = useState({ category: 'grocery', name: '', distance_label: '', distance_meters: '' });

  const addPlace = () => {
    if (!newPlace.name.trim()) return;
    setForm(prev => ({ ...prev, nearby_places: [...prev.nearby_places, { ...newPlace, name: newPlace.name.trim() }] }));
    setNewPlace({ category: 'grocery', name: '', distance_label: '', distance_meters: '' });
  };

  const removePlace = (i) => setForm(prev => ({ ...prev, nearby_places: prev.nearby_places.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <div className="kb-section">
        <div className="kb-section__title">🚌 Transit & Walkability</div>
        <div className="kb-form-grid kb-form-grid--2">
          <Field label="Transit Info" span={2}>
            <textarea className="kb-textarea" value={form.transit_info} onChange={e => set('transit_info', e.target.value)}
              placeholder="Close to C-Train station (2 min walk), multiple bus routes on 14th Ave…" />
          </Field>
          <Field label="Walk Score (0–100)">
            <input className="kb-input" type="number" min={0} max={100} value={form.walk_score} onChange={e => set('walk_score', +e.target.value)} placeholder="85" />
          </Field>
        </div>
      </div>

      <div className="kb-section">
        <div className="kb-section__title">📍 Nearby Places</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <select className="kb-select" value={newPlace.category} onChange={e => setNewPlace(p => ({ ...p, category: e.target.value }))} style={{ minWidth: 140 }}>
            {NEARBY_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.value.replace('_', ' ')}</option>)}
          </select>
          <input className="kb-input" style={{ flex: 1, minWidth: 160 }} value={newPlace.name} onChange={e => setNewPlace(p => ({ ...p, name: e.target.value }))} placeholder="Place name" />
          <input className="kb-input" style={{ width: 130 }} value={newPlace.distance_label} onChange={e => setNewPlace(p => ({ ...p, distance_label: e.target.value }))} placeholder="5 min walk" />
          <button className="kb-btn kb-btn--primary" onClick={addPlace}>
            <FeatherIcon icon="plus" size={14} /> Add
          </button>
        </div>

        {form.nearby_places?.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px', color: '#9CA3AF', fontSize: 14 }}>
            No nearby places added yet.
          </div>
        )}

        <div className="kb-nearby-grid">
          {(form.nearby_places || []).map((p, i) => (
            <NearbyPlaceRow key={i} place={p} onRemove={() => removePlace(i)} />
          ))}
        </div>
      </div>
    </div>
  );
};

const CommunityTab = ({ form, set, setForm }) => {
  const [newEvent, setNewEvent] = useState({ title: '', description: '', event_date: '', location: '', rsvp_url: '', is_residents_only: false });

  const addEvent = () => {
    if (!newEvent.title.trim() || !newEvent.event_date) return;
    setForm(prev => ({ ...prev, events: [...prev.events, { ...newEvent, _tempId: Date.now() }] }));
    setNewEvent({ title: '', description: '', event_date: '', location: '', rsvp_url: '', is_residents_only: false });
  };

  const removeEvent = (i) => setForm(prev => ({ ...prev, events: prev.events.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <div className="kb-section">
        <div className="kb-section__title">👤 Community Leader</div>
        <div className="kb-form-grid kb-form-grid--2">
          <Field label="Name">
            <input className="kb-input" value={form.community_leader?.name || ''} onChange={e => set('community_leader.name', e.target.value)} placeholder="Jane Smith" />
          </Field>
          <Field label="Title / Role">
            <input className="kb-input" value={form.community_leader?.office_hours || ''} onChange={e => set('community_leader.office_hours', e.target.value)} placeholder="Mon–Fri 9AM–5PM" />
          </Field>
          <Field label="Email">
            <input className="kb-input" type="email" value={form.community_leader?.email || ''} onChange={e => set('community_leader.email', e.target.value)} placeholder="jane@property.com" />
          </Field>
          <Field label="Phone">
            <input className="kb-input" value={form.community_leader?.phone || ''} onChange={e => set('community_leader.phone', e.target.value)} placeholder="+1 403 555 0123" />
          </Field>
          <Field label="Bio" span={2}>
            <textarea className="kb-textarea" value={form.community_leader?.bio || ''} onChange={e => set('community_leader.bio', e.target.value)} placeholder="Brief introduction for the AI to share when residents ask about the team…" />
          </Field>
        </div>
      </div>

      <div className="kb-section">
        <div className="kb-section__title">🎉 Community Events</div>
        <div style={{ border: '1.5px solid #E5E7EB', borderRadius: 12, padding: 16, marginBottom: 16, background: '#F9FAFB' }}>
          <div className="kb-form-grid kb-form-grid--2">
            <Field label="Event Title" required>
              <input className="kb-input" value={newEvent.title} onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} placeholder="Summer BBQ" />
            </Field>
            <Field label="Date" required>
              <input className="kb-input" type="datetime-local" value={newEvent.event_date} onChange={e => setNewEvent(p => ({ ...p, event_date: e.target.value }))} />
            </Field>
            <Field label="Location">
              <input className="kb-input" value={newEvent.location} onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))} placeholder="Rooftop Terrace" />
            </Field>
            <Field label="RSVP URL">
              <input className="kb-input" value={newEvent.rsvp_url} onChange={e => setNewEvent(p => ({ ...p, rsvp_url: e.target.value }))} placeholder="https://…" />
            </Field>
            <Field label="Description" span={2}>
              <textarea className="kb-textarea" style={{ minHeight: 60 }} value={newEvent.description} onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} placeholder="Join us for a community BBQ with…" />
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <Toggle checked={newEvent.is_residents_only} onChange={v => setNewEvent(p => ({ ...p, is_residents_only: v }))} label="Residents Only" />
            <button className="kb-btn kb-btn--primary" onClick={addEvent}>
              <FeatherIcon icon="plus" size={14} /> Add Event
            </button>
          </div>
        </div>

        <div className="kb-events">
          {(form.events || []).length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: 14 }}>No events scheduled.</div>
          )}
          {(form.events || []).map((ev, i) => {
            const d = ev.event_date ? new Date(ev.event_date) : null;
            return (
              <div key={ev._id || ev._tempId || i} className="kb-event-card">
                <div className="kb-event-card__date">
                  <span className="kb-event-card__date-day">{d ? d.getDate() : '–'}</span>
                  <span className="kb-event-card__date-mon">{d ? d.toLocaleString('en', { month: 'short' }).toUpperCase() : '–'}</span>
                </div>
                <div className="kb-event-card__info">
                  <div className="kb-event-card__title">{ev.title}</div>
                  <div className="kb-event-card__desc">{ev.description || ev.location || 'No description'}</div>
                  <div className="kb-event-card__badges">
                    {ev.location && <span className="kb-event-card__badge">📍 {ev.location}</span>}
                    {ev.is_residents_only && <span className="kb-event-card__badge kb-event-card__badge--private">🔒 Residents Only</span>}
                  </div>
                </div>
                <button className="kb-event-card__remove" onClick={() => removeEvent(i)}>×</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const AIContentTab = ({ form, set, setForm }) => (
  <div>
    <div className="kb-section">
      <div className="kb-section__title">🤖 AI Summary & Highlights</div>
      <Field label="AI Summary" hint="A rich paragraph the AI uses to describe this property in conversation.">
        <textarea className="kb-textarea" style={{ minHeight: 100 }} value={form.ai_summary} onChange={e => set('ai_summary', e.target.value)}
          placeholder="The Summit is a luxury high-rise in the heart of Beltline offering stunning city views, in-suite laundry, and premium finishes…" />
      </Field>

      <div style={{ marginTop: 16 }}>
        <div className="kb-field__label" style={{ marginBottom: 8 }}>AI Highlights</div>
        <ListEditor items={form.ai_highlights} onChange={v => set('ai_highlights', v)} placeholder="e.g. Stunning panoramic views from every unit" />
      </div>
    </div>

    <div className="kb-section">
      <div className="kb-section__title">🏆 Why Choose Us</div>
      <Field label="Headline" hint="Bold value proposition the AI leads with.">
        <input className="kb-input" value={form.why_choose_us?.headline || ''} onChange={e => set('why_choose_us.headline', e.target.value)}
          placeholder="Experience elevated city living at its finest" />
      </Field>
      <div style={{ marginTop: 12 }}>
        <div className="kb-field__label" style={{ marginBottom: 8 }}>Selling Points</div>
        <ListEditor items={form.why_choose_us?.points || []} onChange={v => set('why_choose_us.points', v)} placeholder="e.g. 24/7 concierge service" />
      </div>
    </div>

    <div className="kb-section">
      <div className="kb-section__title">⭐ Property Features</div>
      <ListEditor items={form.property_features} onChange={v => set('property_features', v)} placeholder="e.g. Floor-to-ceiling windows" />
    </div>

    <div className="kb-section">
      <div className="kb-section__title">❓ FAQs</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        {(form.faqs || []).length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: 14 }}>No FAQs yet. Add common questions tenants ask.</div>
        )}
        {(form.faqs || []).map((faq, i) => (
          <div key={i} className="kb-faq-item">
            <div className="kb-faq-item__num">{i + 1}</div>
            <div className="kb-faq-item__fields">
              <input className="kb-input" value={faq.question} onChange={e => {
                const f = [...form.faqs]; f[i] = { ...f[i], question: e.target.value };
                setForm(prev => ({ ...prev, faqs: f }));
              }} placeholder="Question…" />
              <textarea className="kb-textarea" style={{ minHeight: 60 }} value={faq.answer} onChange={e => {
                const f = [...form.faqs]; f[i] = { ...f[i], answer: e.target.value };
                setForm(prev => ({ ...prev, faqs: f }));
              }} placeholder="Answer…" />
            </div>
            <button className="kb-faq-item__remove" onClick={() => setForm(prev => ({ ...prev, faqs: prev.faqs.filter((_, idx) => idx !== i) }))}>×</button>
          </div>
        ))}
      </div>
      <button className="kb-btn kb-btn--ghost" style={{ width: '100%', justifyContent: 'center' }}
        onClick={() => setForm(prev => ({ ...prev, faqs: [...prev.faqs, { question: '', answer: '' }] }))}>
        <FeatherIcon icon="plus" size={14} /> Add FAQ
      </button>
    </div>
  </div>
);

const ACTION_LINK_FIELDS = [
  { key: 'property_page_url', label: 'Property Page', icon: '🌐' },
  { key: 'book_tour_url', label: 'Book a Tour', icon: '📅' },
  { key: 'apply_now_url', label: 'Apply Now', icon: '📝' },
  { key: 'virtual_tour_url', label: 'Virtual Tour', icon: '🎥' },
  { key: 'request_maintenance_url', label: 'Maintenance Request', icon: '🔧' },
  { key: 'get_insurance_url', label: 'Get Insurance', icon: '🛡️' },
  { key: 'discount_cards_url', label: 'Discount Cards', icon: '🎁' },
];

const ActionLinksTab = ({ form, set }) => (
  <div>
    <div className="kb-section">
      <div className="kb-section__title">🔗 Action Links</div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
        These URLs are surfaced by the AI when tenants ask how to book a tour, apply, or access services.
      </div>
      <div className="kb-action-links">
        {ACTION_LINK_FIELDS.map(({ key, label, icon }) => (
          <div key={key} className="kb-action-link-row">
            <div className="kb-action-link-row__icon">{icon}</div>
            <div className="kb-action-link-row__label">{label}</div>
            <input
              value={form.action_links?.[key] || ''}
              onChange={e => set(`action_links.${key}`, e.target.value)}
              placeholder="https://…"
            />
          </div>
        ))}
      </div>
    </div>

    <div className="kb-section">
      <div className="kb-section__title">📐 Floor Plan Apply URLs</div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
        Map a specific apply URL to each floor plan label (for floor plan-level CTAs).
      </div>
      {(form.floor_plans || []).length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: 14 }}>
          Add floor plans in the Floor Plans tab first.
        </div>
      )}
      <div className="kb-action-links">
        {(form.floor_plans || []).map((plan, i) => (
          <div key={plan._id || plan._tempId || i} className="kb-action-link-row">
            <div className="kb-action-link-row__icon">📐</div>
            <div className="kb-action-link-row__label">{plan.label || `Plan ${i + 1}`}</div>
            <input
              value={form.action_links?.floor_plan_apply_urls?.[plan.label] || ''}
              onChange={e => set(`action_links.floor_plan_apply_urls`, {
                ...(form.action_links?.floor_plan_apply_urls || {}),
                [plan.label]: e.target.value,
              })}
              placeholder="https://apply.property.com?plan=…"
            />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export { KnowledgeBaseFormModal };